/**
 * Payment Checkout Page
 * Handles Cashfree payment using Drop-in Checkout
 */

import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackInitiateCheckout } from '../utils/metaPixel';
import { getCashfreeSdkMode } from '../components/CreditSystem/creditCheckoutUtils';
import { SEO } from '../shared/components/SEO';

// Declare Cashfree type
declare global {
  interface Window {
    Cashfree: (config: { mode: string }) => {
      checkout: (options: {
        paymentSessionId: string;
        returnUrl: string;
        redirectTarget?: string;
      }) => Promise<{
        error?: { message?: string };
        redirect?: boolean;
        paymentDetails?: unknown;
      }>;
    };
  }
}

const PaymentCheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 🔒 CRITICAL: Set payment in progress flag to prevent session timeout logout
    localStorage.setItem('payment_in_progress', 'true');
    console.log('🛡️ Payment flow started - session timeout disabled');
    
    // Get payment data: first from sessionStorage (PricingPage flow), then from URL (Auth/AuthConfirm redirect with short_url)
    const paymentDataStr = sessionStorage.getItem('pending_payment');
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = urlParams.get('session_id');
    const orderIdFromUrl = urlParams.get('order_id');

    let sessionId: string;
    let orderId: string;

    if (paymentDataStr) {
      try {
        const paymentData = JSON.parse(paymentDataStr) as { sessionId: string; orderId: string; subscriptionId?: string };
        sessionId = paymentData.sessionId;
        orderId = paymentData.orderId;
      } catch {
        setError(t('views.paymentCheckout.invalidSession'));
        localStorage.removeItem('payment_in_progress');
        return;
      }
    } else if (sessionIdFromUrl && orderIdFromUrl) {
      sessionId = sessionIdFromUrl;
      orderId = orderIdFromUrl;
      // Persist to sessionStorage so reload/back still works
      sessionStorage.setItem('pending_payment', JSON.stringify({ sessionId, orderId }));
    } else {
      setError(t('views.paymentCheckout.noSessionFound'));
      localStorage.removeItem('payment_in_progress');
      return;
    }

    if (!sessionId || !orderId) {
      setError(t('views.paymentCheckout.invalidSession'));
      localStorage.removeItem('payment_in_progress');
      return;
    }

    // Meta Pixel: user has a valid session and is about to see the payment UI
    trackInitiateCheckout();

    // Load Cashfree SDK
    const loadCashfreeSDK = async () => {
      try {
        // Load SDK script
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        
        await new Promise<void>((resolve, reject) => {
          script.onload = () => resolve();
          script.onerror = () => reject(new Error(t('views.paymentCheckout.failedToLoadSdk')));
          document.body.appendChild(script);
        });

        // Initialize Cashfree — prefer cf_mode from URL (for native app redirect from Capacitor),
        // else use VITE_B2C_CASHFREE_ENV / host-based detection
        const urlMode = urlParams.get('cf_mode');
        const cashfreeMode = urlMode === 'sandbox' || urlMode === 'production'
          ? urlMode
          : getCashfreeSdkMode();
        const cashfree = window.Cashfree({
          mode: cashfreeMode
        });

        // Payment options
        const paymentOptions = {
          paymentSessionId: sessionId,
          returnUrl: `${window.location.origin}/payment-success?type=purchase&order_id=${orderId}`,
          redirectTarget: '_self' // Redirect in same tab
        };

        console.log('Initializing Cashfree checkout with:', paymentOptions);

        // Open checkout
        const result = await cashfree.checkout(paymentOptions);

        if (result.error) {
          console.error('Payment failed:', result.error);
          setError(result.error.message || t('views.paymentCheckout.paymentFailed'));
          // Clear payment flag on error
          localStorage.removeItem('payment_in_progress');
        } else if (result.redirect) {
          console.log('Redirecting to payment page...');
          // Cashfree will handle the redirect
          // Keep payment_in_progress flag active during redirect
        } else if (result.paymentDetails) {
          console.log('Payment completed:', result.paymentDetails);
          // Clear session storage
          sessionStorage.removeItem('pending_payment');
          // Keep payment flag active until success page loads
          // Redirect to success page
          window.location.href = `/payment-success?type=purchase&order_id=${orderId}`;
        }
      } catch (err) {
        console.error('Error initializing Cashfree:', err);
        setError(err instanceof Error ? err.message : t('views.paymentCheckout.failedToInitialize'));
        // Clear payment flag on error
        localStorage.removeItem('payment_in_progress');
      }
    };

      loadCashfreeSDK();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4 sm:p-6 pb-[env(safe-area-inset-bottom)]">
        <SEO
          title="Payment Checkout — GoGrabJob"
          description="Complete your GoGrabJob credit purchase securely via Cashfree checkout."
          canonical="https://www.gograbjob.com/payment-checkout"
          noIndex
        />
        <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
            <AlertCircle className="w-8 h-8" />
            <h2 className="text-xl font-bold">{t('views.paymentCheckout.paymentError')}</h2>
          </div>
          
          <p className="text-gray-700 dark:text-gray-300 mb-6">{error}</p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => window.location.href = '/'}
              className="flex-1 px-4 py-3 min-h-[44px] bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors font-medium touch-manipulation"
            >
              {t('views.paymentCheckout.goHome')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 px-4 py-3 min-h-[44px] text-white rounded-lg transition-colors font-medium touch-manipulation"
              style={{ background: 'var(--brand-cta-bg)' }}
            >
              {t('views.paymentCheckout.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-4 sm:p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <SEO
        title="Payment Checkout — GoGrabJob"
        description="Complete your GoGrabJob credit purchase securely via Cashfree checkout."
        canonical="https://www.gograbjob.com/payment-checkout"
        noIndex
      />
      <div className="w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 sm:p-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            You&apos;re almost there.
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            {t('views.paymentCheckout.pleaseWait')}
          </p>
          <Loader2 className="w-14 h-14 sm:w-16 sm:h-16 text-violet-600 dark:text-violet-400 animate-spin mx-auto mb-6" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('views.paymentCheckout.preparingPayment')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {t('views.paymentCheckout.mayTakeSeconds')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckoutPage;

